import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  FileText,
  Check,
  AlertCircle,
  Loader2,
  Download,
  Eye,
  Printer,
  ChevronRight,
  Settings,
  CheckSquare,
  Square
} from 'lucide-react';
import { getAllTemplates, generateDocument } from '../../../services/documentTemplateService';
import jsPDF from 'jspdf';
import logger from '../../../utils/logger';

const EnhancedDocumentTemplateSelector = ({ reservation, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [showSectionCustomizer, setShowSectionCustomizer] = useState(false);
  const [customSections, setCustomSections] = useState({});

  useEffect(() => {
    fetchActiveTemplates();
  }, []);

  const fetchActiveTemplates = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getAllTemplates({ status: 'active' });
      setTemplates(data);
    } catch (err) {
      logger.error('Error loading templates:', err);
      setError('Error al cargar las plantillas disponibles');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Inicializar secciones personalizables (EXCLUYENDO las obligatorias)
    // Las secciones obligatorias son: titular (includeClient) y tramos (se manejan automáticamente)
    const defaultSections = template.body_sections || {};

    // Filtrar solo las secciones opcionales (no obligatorias)
    // IMPORTANTE: Heredar los valores de la plantilla
    const optionalSections = {
      includePassengers: defaultSections.includePassengers === true,
      includeHotels: defaultSections.includeHotels === true,
      includeFlights: defaultSections.includeFlights === true,
      includeTours: defaultSections.includeTours === true,
      includeTransfers: defaultSections.includeTransfers === true,
      includePayment: defaultSections.includePayment === true,
      includeAssistance: defaultSections.includeAssistance === true
    };

    setCustomSections(optionalSections);
    setShowSectionCustomizer(false);
  };

  const toggleSection = (sectionKey) => {
    setCustomSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleGenerateDocument = async () => {
    if (!selectedTemplate) {
      setError('Por favor selecciona una plantilla');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedDocument(null);

    try {
      // Verificar si el usuario personalizó alguna sección opcional
      // (Si todas están en false, usar las secciones de la plantilla por defecto)
      const hasCustomizations = Object.values(customSections).some(value => value === true);

      let sectionsToSend = null;

      if (hasCustomizations) {
        // El usuario personalizó: enviar customSections
        sectionsToSend = {
          includeClient: true, // OBLIGATORIO: Titular de la reserva
          includeSegments: true, // OBLIGATORIO: Tramos con origen, destino y fechas
          ...customSections // Secciones personalizables por el usuario
        };
      }
      // Si no hay personalizaciones (todas en false), NO enviar customSections
      // El backend usará las secciones de la plantilla por defecto

      // Enviar al backend (con o sin customSections según personalización)
      const result = await generateDocument(selectedTemplate.id, reservation.id, sectionsToSend);
      setGeneratedDocument(result);
    } catch (err) {
      logger.error('Error generating document:', err);
      setError(err.response?.data?.message || 'Error al generar el documento');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenDocument = () => {
    if (generatedDocument) {
      const newWindow = window.open('', '_blank', 'width=900,height=1100');
      if (!newWindow) {
        alert('Por favor, permite ventanas emergentes para visualizar documentos');
        return;
      }
      newWindow.document.write(generatedDocument.htmlContent);
      newWindow.document.title = generatedDocument.documentNumber;
      newWindow.document.close();
    }
  };

  const handlePrintDocument = () => {
    if (generatedDocument) {
      const newWindow = window.open('', '_blank');
      if (!newWindow) {
        alert('Por favor, permite ventanas emergentes para imprimir documentos');
        return;
      }
      newWindow.document.write(generatedDocument.htmlContent);
      newWindow.document.close();
      setTimeout(() => {
        newWindow.print();
      }, 250);
    }
  };

  const handleDownloadHTML = () => {
    if (generatedDocument) {
      const blob = new Blob([generatedDocument.htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${generatedDocument.documentNumber}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadPDF = async () => {
    if (!generatedDocument) return;

    try {
      // Crear un contenedor temporal para renderizar el HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = generatedDocument.htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '210mm'; // A4 width
      document.body.appendChild(tempDiv);

      // Crear PDF usando jsPDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Obtener dimensiones
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convertir HTML a canvas y luego a PDF
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - 20; // Márgenes de 10mm a cada lado
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10; // Margen superior

      // Agregar imagen al PDF (con paginación si es necesario)
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Descargar PDF
      pdf.save(`${generatedDocument.documentNumber}.pdf`);

      // Limpiar
      document.body.removeChild(tempDiv);
    } catch (error) {
      logger.error('Error generating PDF:', error);
      alert('Error al generar el PDF. Por favor, intenta descargar como HTML.');
    }
  };

  // Importar html2canvas dinámicamente
  const html2canvas = async (element, options) => {
    const html2canvasModule = await import('html2canvas');
    return html2canvasModule.default(element, options);
  };

  const getRecommendedTemplates = () => {
    if (!reservation._original) return templates;

    const hasFlights = reservation._original.reservation_flights?.length > 0;
    const hasHotels = reservation._original.reservation_hotels?.length > 0;
    const hasTours = reservation._original.reservation_tours?.length > 0;

    return templates.filter(template => {
      const sections = template.body_sections || {};
      if (hasFlights && sections.includeFlights) return true;
      if (hasHotels && sections.includeHotels) return true;
      if (hasTours && sections.includeTours) return true;
      return true;
    });
  };

  const recommendedTemplates = getRecommendedTemplates();

  const sectionLabels = {
    // Secciones personalizables (las obligatorias no aparecen aquí)
    includePassengers: 'Lista de Pasajeros',
    includeHotels: 'Información de Hoteles',
    includeFlights: 'Itinerarios de Vuelos',
    includeTours: 'Tours y Actividades',
    includeTransfers: 'Traslados',
    includePayment: 'Información de Pago',
    includeAssistance: 'Asistencia Médica'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="w-7 h-7" />
                Generar Documento Personalizado
              </h2>
              <p className="text-blue-100 mt-1">
                Reserva #{reservation.id} - {reservation.clientName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              disabled={isGenerating}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          {/* Documento generado - Vista previa */}
          {generatedDocument && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-lg"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-bold text-green-900">¡Documento Generado Exitosamente!</h3>
                  </div>
                  <p className="text-green-700">
                    <span className="font-semibold">Número:</span> {generatedDocument.documentNumber}
                  </p>
                  <p className="text-green-700 text-sm">
                    <span className="font-semibold">Plantilla:</span> {generatedDocument.template?.name} (v{generatedDocument.template?.version})
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <motion.button
                  onClick={handleOpenDocument}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Eye className="w-4 h-4" />
                  Ver Documento
                </motion.button>

                <motion.button
                  onClick={handlePrintDocument}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Printer className="w-4 h-4" />
                  Imprimir
                </motion.button>

                <motion.button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </motion.button>

                <motion.button
                  onClick={handleDownloadHTML}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Download className="w-4 h-4" />
                  Descargar HTML
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Selección de plantilla */}
          {!generatedDocument && (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                1. Selecciona el tipo de documento a generar
              </h3>

              {isLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">Cargando plantillas disponibles...</p>
                </div>
              ) : recommendedTemplates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No hay plantillas activas disponibles</p>
                  <p className="text-gray-400 mt-2 text-sm">
                    Contacta al administrador para crear plantillas de documentos
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {recommendedTemplates.map((template) => {
                    const isSelected = selectedTemplate?.id === template.id;

                    return (
                      <motion.div
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-lg'
                            : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}

                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                              {template.code}
                            </span>
                            <span className="text-xs text-gray-500">v{template.version}</span>
                          </div>
                          <h4 className="font-semibold text-gray-900">{template.name}</h4>
                          {template.description && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {template.description}
                            </p>
                          )}
                        </div>

                        <div className="text-xs text-gray-500">
                          Siguiente: {template.number_format?.replace(/#+/g, template.next_number?.toString().padStart(4, '0'))}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Personalizador de secciones */}
              {selectedTemplate && (
                <div className="mb-6">
                  {/* Información de secciones obligatorias */}
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2 text-sm">
                      📋 Información Incluida Automáticamente
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>✓ <strong>Titular de la reserva:</strong> Nombre, cédula, contacto</li>
                      <li>✓ <strong>Tramos del viaje:</strong> Ciudades de origen y destino con fechas (multidestino compatible)</li>
                    </ul>
                    <p className="text-xs text-blue-700 mt-2 italic">
                      Estas secciones siempre se incluyen en todos los documentos generados.
                    </p>
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      2. Personaliza las secciones adicionales
                    </h3>
                    <button
                      onClick={() => setShowSectionCustomizer(!showSectionCustomizer)}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {showSectionCustomizer ? 'Ocultar' : 'Mostrar'} opciones
                    </button>
                  </div>

                  <AnimatePresence>
                    {showSectionCustomizer && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(customSections).map(([key, value]) => {
                            // Verificar si esta sección viene predeterminada en la plantilla
                            const isPredefined = selectedTemplate?.body_sections?.[key] === true;

                            return (
                              <label
                                key={key}
                                className={`flex items-center gap-2 p-3 bg-white border rounded-lg transition-colors ${
                                  isPredefined
                                    ? 'border-green-300 bg-green-50 cursor-not-allowed opacity-75'
                                    : 'border-gray-300 cursor-pointer hover:bg-gray-50'
                                }`}
                                title={isPredefined ? 'Esta sección está incluida por defecto en la plantilla' : ''}
                              >
                                <button
                                  type="button"
                                  onClick={() => !isPredefined && toggleSection(key)}
                                  className="flex-shrink-0"
                                  disabled={isPredefined}
                                >
                                  {value ? (
                                    <CheckSquare className={`w-5 h-5 ${isPredefined ? 'text-green-600' : 'text-blue-600'}`} />
                                  ) : (
                                    <Square className="w-5 h-5 text-gray-400" />
                                  )}
                                </button>
                                <span className={`text-sm flex-1 ${isPredefined ? 'text-green-800 font-medium' : 'text-gray-700'}`}>
                                  {sectionLabels[key] || key.replace('include', '')}
                                  {isPredefined && (
                                    <span className="block text-xs text-green-600 mt-0.5">
                                      Incluida en plantilla
                                    </span>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          Las secciones seleccionadas se incluirán en el documento generado. Las secciones en verde ya están incluidas en la plantilla.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Resumen de secciones seleccionadas */}
                  {!showSectionCustomizer && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(customSections)
                        .filter(([key, value]) => value)
                        .map(([key]) => {
                          const isPredefined = selectedTemplate?.body_sections?.[key] === true;
                          return (
                            <span
                              key={key}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                isPredefined
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                              title={isPredefined ? 'Incluida en la plantilla' : 'Añadida manualmente'}
                            >
                              <Check className="w-3 h-3" />
                              {sectionLabels[key] || key.replace('include', '')}
                            </span>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!generatedDocument && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {selectedTemplate ? (
                <>
                  Listo para generar: <span className="font-semibold">{selectedTemplate.name}</span>
                </>
              ) : (
                'Selecciona una plantilla para continuar'
              )}
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                disabled={isGenerating}
              >
                Cancelar
              </button>
              <motion.button
                onClick={handleGenerateDocument}
                disabled={!selectedTemplate || isGenerating}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                whileHover={{ scale: !selectedTemplate || isGenerating ? 1 : 1.02 }}
                whileTap={{ scale: !selectedTemplate || isGenerating ? 1 : 0.98 }}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    Generar Documento
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>
            </div>
          </div>
        )}

        {/* Footer cuando el documento está generado */}
        {generatedDocument && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              onClick={() => {
                setGeneratedDocument(null);
                setSelectedTemplate(null);
              }}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              Generar Otro Documento
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Cerrar
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default EnhancedDocumentTemplateSelector;
